import { z } from 'zod';
export const deviceIcons = [
    'monitor',
    'tower',
    'laptop',
    'server',
    'nas',
    'mini',
    'gpu',
    'tv',
    'tablet',
    'phone',
    'cloud',
    'gamepad',
    'router',
];
export const deviceColors = ['mint', 'sky', 'violet', 'rose', 'lime', 'slate'];
export const defaultDeviceGroup = 'My devices';
export const customizationLimit = 500;
const label = (max) => z
    .string()
    .trim()
    .transform((value) => value.replace(/\s+/g, ' '))
    .pipe(z.string().min(1).max(max));
export const customizationSchema = z
    .object({
    name: label(80).optional(),
    group: label(60).optional(),
    icon: z.enum(deviceIcons).optional(),
    color: z.enum(deviceColors).optional(),
})
    .strict();
export const directorySchema = z
    .object({
    view: z.enum(['grid', 'list']),
    sort: z.enum(['name', 'status', 'seen']),
    collapsed: z
        .array(label(60))
        .max(200)
        .transform((value) => [...new Set(value)]),
})
    .strict();
export const terminalSchema = z
    .object({
    fontSize: z.number().int().min(10).max(24),
    keyBar: z.boolean().nullable(), // null follows the current device's pointer type
})
    .strict();
/** How the person appears in their own client. Identity itself always comes from ATUM Auth. */
export const accountSchema = z
    .object({
    displayName: z
        .string()
        .trim()
        .transform((value) => value.replace(/\s+/g, ' '))
        .pipe(z.string().max(60)), // empty means "use the account name"
    avatarColor: z.enum(deviceColors),
})
    .strict();
export const defaultAccountSettings = () => ({ displayName: '', avatarColor: 'mint' });
export const settingsSchema = z
    .object({
    theme: z.enum(['dark', 'light', 'system']),
    directory: directorySchema,
    terminal: terminalSchema,
    account: accountSchema.default(defaultAccountSettings),
})
    .strict();
export const settingsPatchSchema = z
    .object({
    theme: settingsSchema.shape.theme.optional(),
    directory: directorySchema.partial().optional(),
    terminal: terminalSchema.partial().optional(),
    account: accountSchema.partial().optional(),
})
    .strict();
export const customizationMapSchema = z
    .record(z.uuid(), customizationSchema)
    .refine((value) => Object.keys(value).length <= customizationLimit, 'Too many customized devices.');
export const preferencesSchema = z
    .object({
    revision: z.number().int().min(0).max(2147483646),
    settings: settingsSchema,
    customizations: customizationMapSchema,
})
    .strict();
export const mutationSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('settings'), patch: settingsPatchSchema }).strict(),
    z.object({ type: z.literal('device'), deviceId: z.uuid(), value: customizationSchema }).strict(),
    z.object({ type: z.literal('reset-device'), deviceId: z.uuid() }).strict(),
    z.object({ type: z.literal('rename-group'), from: label(60), to: label(60) }).strict(),
    z
        .object({
        type: z.literal('import'),
        settings: settingsSchema,
        customizations: customizationMapSchema,
    })
        .strict(),
]);
export const preferencesWriteSchema = z
    .object({
    expectedRevision: preferencesSchema.shape.revision,
    mutation: mutationSchema,
})
    .strict();
export function defaultPreferences() {
    return {
        revision: 0,
        settings: {
            theme: 'dark',
            directory: { view: 'grid', sort: 'name', collapsed: [] },
            terminal: { fontSize: 15, keyBar: null },
            account: defaultAccountSettings(),
        },
        customizations: {},
    };
}
//# sourceMappingURL=index.js.map